import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useDevelopmentLogs, useSaveDevelopmentLogs } from '../hooks/use-tickets';
import { useToast } from './ui/toast';
import type { DevelopmentLog, DevelopmentLogEntry } from '../types';
import { HOURS_WHEEL_STEP, stepHours } from '../lib/hours-step';

/** Format an ISO datetime to YYYY-MM-DD (date part only). */
function toInputDate(iso: string): string {
  return iso.slice(0, 10);
}

function todayInputDate(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

interface HoursInputProps {
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
}

/**
 * Hours input for one table row (sugestie cmuzontxd): when focused, the mouse
 * wheel (and the arrow keys) increment / decrement the value by 0.25h. Manual typing still accepts
 * any decimal (step="any") so existing values like 0.35 stay valid
 * (sugestie cmuf6tqo). The wheel is only captured while the input has focus,
 * so hovering over it never hijacks the page scroll.
 */
function HoursInput({ value, disabled, onChange }: HoursInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const latest = useRef({ value, disabled, onChange });
  latest.current = { value, disabled, onChange };

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    // Native listener: React's onWheel is passive and cannot preventDefault.
    const handleWheel = (e: WheelEvent) => {
      const { value: current, disabled: isDisabled, onChange: emit } = latest.current;
      if (isDisabled || document.activeElement !== el || e.deltaY === 0) return;
      e.preventDefault();
      emit(stepHours(current, e.deltaY < 0 ? 'up' : 'down'));
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <Input
      ref={inputRef}
      type="number"
      min="0"
      step="any"
      placeholder="ore"
      title={`Rotița mouse-ului (cu câmpul selectat) modifică valoarea cu ${HOURS_WHEEL_STEP}h`}
      value={value || ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
      onKeyDown={(e) => {
        if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
        // Arrow keys use the same 0.25h step as the wheel (step="any" would use 1).
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          if (!disabled) onChange(stepHours(value, e.key === 'ArrowUp' ? 'up' : 'down'));
        }
      }}
      className="w-20 shrink-0 px-2 py-1.5 text-xs"
    />
  );
}

interface DevelopmentLogTableProps {
  ticketId: string;
  /** When false the table is rendered read-only (client portal / no permission). */
  editable: boolean;
  /** The synced total (ticket.developmentDuration) — displayed read-only. */
  total: number | null | undefined;
  /**
   * Optional ref kept in sync with the current (unsaved) rows, so the parent
   * can auto-save them before a transition (e.g. "Trimite la review").
   */
  rowsRef?: MutableRefObject<DevelopmentLogEntry[] | null>;
}

/**
 * Tabel ore dezvoltare (sugestie cmu2n29m9): rows of date + duration + description.
 * Date and duration are required. The ticket's developmentDuration is the
 * automatic sum of the rows (re-synced server-side on save).
 */
export function DevelopmentLogTable({ ticketId, editable, total, rowsRef }: DevelopmentLogTableProps) {
  const { toast } = useToast();
  const { data: logsData } = useDevelopmentLogs(ticketId);
  const saveLogs = useSaveDevelopmentLogs({
    onSuccess: () => toast('success', 'Orele de dezvoltare au fost salvate'),
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });

  const logs: DevelopmentLog[] = logsData?.data ?? [];
  const [rows, setRows] = useState<DevelopmentLogEntry[] | null>(null);

  // Initialize editable rows once the logs are loaded
  const effectiveRows: DevelopmentLogEntry[] = rows ?? logs.map((l) => ({
    id: l.id,
    logDate: toInputDate(l.logDate),
    duration: l.duration,
    description: l.description ?? '',
  }));

  // Keep the parent's ref in sync with the current (possibly unsaved) rows so
  // they can be auto-saved before a transition (sugestie cmuy5xdlr).
  if (rowsRef) rowsRef.current = effectiveRows;

  const setRow = (index: number, patch: Partial<DevelopmentLogEntry>) => {
    setRows(effectiveRows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows([...effectiveRows, { logDate: todayInputDate(), duration: 0, description: '' }]);
  };

  const removeRow = (index: number) => {
    setRows(effectiveRows.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    for (const r of effectiveRows) {
      if (!r.logDate) {
        toast('error', 'Fiecare rând are nevoie de o dată');
        return;
      }
      if (!r.duration || r.duration <= 0) {
        toast('error', 'Fiecare rând are nevoie de o durată pozitivă (ore)');
        return;
      }
    }
    saveLogs.mutate({ ticketId, entries: effectiveRows });
  };

  const sum = effectiveRows.reduce((s, r) => s + (r.duration || 0), 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs text-gray-500 dark:text-dark-muted">Ore dezvoltare (tabel)</label>
        <span className="text-xs font-medium text-gray-700 dark:text-dark-text" title="Suma automată a duratelor din tabel">
          Total: {total != null ? `${total}h` : '—'}
        </span>
      </div>

      {effectiveRows.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-dark-muted">Nicio oră înregistrată.</p>
      ) : (
        <div className="space-y-1.5">
          {effectiveRows.map((r, i) => (
            <div key={r.id ?? `new-${i}`} className="flex items-center gap-1.5">
              <Input
                type="date"
                value={r.logDate}
                disabled={!editable}
                onChange={(e) => setRow(i, { logDate: e.target.value })}
                className="w-[8.5rem] shrink-0 px-2 py-1.5 text-xs"
              />
              <HoursInput
                value={r.duration}
                disabled={!editable}
                onChange={(duration) => setRow(i, { duration })}
              />
              <Input
                type="text"
                placeholder="Descriere (opțional)"
                value={r.description ?? ''}
                disabled={!editable}
                onChange={(e) => setRow(i, { description: e.target.value })}
                className="min-w-0 flex-1 px-2 py-1.5 text-xs"
              />
              {editable && (
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  title="Șterge rândul"
                  className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Buttons keep their text on one line (sugestie cmuzontxd); the
              "Total nou" hint wraps to its own line instead of squeezing them. */}
          <Button size="sm" variant="outline" onClick={addRow} className="shrink-0 whitespace-nowrap">
            <Plus size={14} /> Adaugă rând
          </Button>
          <Button size="sm" disabled={saveLogs.isPending} onClick={handleSave} className="shrink-0 whitespace-nowrap">
            {saveLogs.isPending ? 'Se salvează...' : 'Salvează ore'}
          </Button>
          {Math.abs(sum - (total ?? 0)) > 0.001 && (
            <span className="whitespace-nowrap text-xs text-amber-600 dark:text-amber-400">
              Total nou: {sum}h (nesalvat)
            </span>
          )}
        </div>
      )}
    </div>
  );
}
